import React from 'react';
import {Card, Form, Row, Col, Segmented, Typography, Checkbox} from 'antd';
import InputComponent from '../../../atoms/Inputs/Input';
import SelectComponent from '../../../atoms/Select';
import {dropDownOption1} from '../../../../utils/types';
import styles from './style.module.css';
import {useIsMobile} from "../../../../utils/helpers";

const {Paragraph} = Typography;

interface FormProps {
    accountType: 'individual' | 'business';
    onAccountTypeChange: (v: 'individual' | 'business') => void;
    firstName: string;
    onFirstName: (v: string) => void;
    lastName: string;
    onLastName: (v: string) => void;
    username: string;
    onUsernameChange: (v: string) => void;
    usernameError: string | null;
    email: string;
    onEmailChange: (v: string) => void;
    emailValid: boolean;
    phone: string;
    onPhoneChange: (v: string) => void;
    companyName: string;
    onCompanyNameChange: (v: string) => void;
    companyWebsite: string;
    onCompanyWebsiteChange: (v: string) => void;
    vatNumber: string;
    onVatNumberChange: (v: string) => void;
    companyAddress: string;
    onCompanyAddressChange: (v: string) => void;
    roleType: string;
    onRoleTypeChange: (v: string) => void;
    lockedRoleType?: string;
    required: boolean;
    onSubmit: (e: React.FormEvent) => Promise<void>;
    onOpenDialog: (mode: string) => void;
    onClose: () => void;
    agreementChecked: boolean;
    onAgreementChange: (v: boolean) => void;
}

const userTypeItems: dropDownOption1[] = [
    {label: 'Advertiser', value: 'advertiser'},
    {label: 'Promoter', value: 'promoter'},
    {label: 'Partner', value: 'partner'}
];

const RegistrationForm: React.FC<FormProps> = ({
                                                   accountType, onAccountTypeChange,
                                                   firstName, onFirstName,
                                                   lastName, onLastName,
                                                   username, onUsernameChange, usernameError,
                                                   email, onEmailChange, emailValid,
                                                   phone, onPhoneChange,
                                                   companyName, onCompanyNameChange,
                                                   companyWebsite, onCompanyWebsiteChange,
                                                   vatNumber, onVatNumberChange,
                                                   companyAddress, onCompanyAddressChange,
                                                   roleType, onRoleTypeChange, lockedRoleType,
                                                   required, onSubmit,
                                                   onOpenDialog, onClose, agreementChecked, onAgreementChange
                                               }) => {

    const handleShowLogin = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        onClose();
        onOpenDialog('login');
    };

    const ITEM_STYLE = {marginBottom: 0};
    const isMobile = useIsMobile();

    return (
        <Card title={<span style={{color: '#1677ff', fontWeight: 'bold', fontSize: 28}}>Sign up</span>}
              bodyStyle={{padding: 16}}
        >
            <Form layout="vertical" onFinish={onSubmit} className={styles.registrationForm}>
                <Form.Item style={ITEM_STYLE}>
                    <Segmented
                        options={[
                            {label: 'Individual', value: 'individual'},
                            {label: 'Business', value: 'business'}
                        ]}
                        value={accountType}
                        onChange={v => onAccountTypeChange(v as any)}
                    />
                </Form.Item>

                <Row gutter={[12, 1]}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Username"
                            required
                            tooltip="Your public handle"
                            style={ITEM_STYLE}
                            validateStatus={usernameError ? 'error' : undefined}
                            help={usernameError}
                        >
                            <InputComponent
                                label=""
                                placeholder="Enter username"
                                value={username}
                                required={required}
                                onChange={onUsernameChange}
                                labelPosition="up"
                                type="text"
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Email"
                            required
                            validateStatus={!emailValid && email ? 'error' : undefined}
                            help={!emailValid && email ? 'Invalid email' : undefined}
                            style={ITEM_STYLE}
                        >
                            <InputComponent
                                label=""
                                placeholder="you@example.com"
                                value={email}
                                required={required}
                                onChange={onEmailChange}
                                labelPosition="up"
                                type="email"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {accountType === 'individual' ? (
                    <Row gutter={[12, 1]}>
                        <Col xs={24} sm={12}>
                            <Form.Item label="First name" required style={ITEM_STYLE}>
                                <InputComponent
                                    label=""
                                    placeholder="First name"
                                    value={firstName}
                                    required={required}
                                    onChange={onFirstName}
                                    labelPosition="up"
                                    type="text"
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item label="Last name" required style={ITEM_STYLE}>
                                <InputComponent
                                    label=""
                                    placeholder="Last name"
                                    value={lastName}
                                    required={required}
                                    onChange={onLastName}
                                    labelPosition="up"
                                    type="text"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                ) : (
                    <>
                        <Form.Item label="Company name" required style={ITEM_STYLE}>
                            <InputComponent label="" placeholder="Acme Corp" value={companyName} required={required}
                                            onChange={onCompanyNameChange} labelPosition="up" type="text"
                            />
                        </Form.Item>
                        <Row gutter={[12, 1]}>
                            <Col xs={24} sm={12}>
                                <Form.Item label="Website" style={ITEM_STYLE}>
                                    <InputComponent
                                        label=""
                                        placeholder="https://..."
                                        value={companyWebsite}
                                        onChange={onCompanyWebsiteChange}
                                        labelPosition="up"
                                        type="url"
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12}>
                                <Form.Item label="VAT number" style={ITEM_STYLE}>
                                    <InputComponent
                                        label=""
                                        placeholder="VAT #"
                                        value={vatNumber}
                                        onChange={onVatNumberChange}
                                        labelPosition="up"
                                        type="text"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item label="Address" style={ITEM_STYLE}>
                            <InputComponent label="" placeholder="Company address" value={companyAddress}
                                            onChange={onCompanyAddressChange} labelPosition="up" type="text"
                            />
                        </Form.Item>
                    </>
                )}

                <Form.Item label="Phone number" style={ITEM_STYLE}>
                    <InputComponent label="" placeholder="0015551234567" value={phone} onChange={onPhoneChange}
                                    labelPosition="up" type="text"
                    />
                </Form.Item>

                <Form.Item label="User role" required style={ITEM_STYLE}>
                    <SelectComponent label="" placeholder="Select role" value={roleType} onChange={onRoleTypeChange}
                                     options={userTypeItems} required={required} showSearch
                                     disabled={Boolean(lockedRoleType)}
                    />
                </Form.Item>

                <Form.Item
                    name="agreement"
                    valuePropName="checked"
                    rules={[
                        {
                            validator: (_, value) => value ? Promise.resolve() : Promise.reject(
                                new Error("You must agree to Promotify's Terms and Conditions and Privacy Policy"))
                        }
                    ]} style={{marginTop: 8, marginBottom: 8}}
                >
                    <Checkbox
                        checked={agreementChecked}
                        onChange={e => onAgreementChange(e.target.checked)}
                        style={{fontSize: 13}}
                    >
                        I agree with Promotify&apos;s{' '}
                        <a href="/terms-and-conditions"
                           target="_blank"
                           rel="noopener noreferrer"
                           style={{color: '#1677ff'}}
                        >Terms and Conditions</a>
                        {' '}and{' '}
                        <a
                            href="/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{color: '#1677ff'}}
                        >Privacy Policy</a>
                    </Checkbox>
                </Form.Item>

                <Row justify={isMobile ? 'center' : 'end'} style={{marginTop: 12}} className={styles.loginRow}>
                    <Paragraph type="secondary" style={{margin: 0, fontSize: isMobile ? 10 : 14}}>
                        Already have an account?{' '}
                        <a href="#" onClick={handleShowLogin} style={{color: '#1677ff'}}>Log in</a>
                    </Paragraph>
                </Row>
            </Form>
        </Card>
    );
};

export default RegistrationForm;